import { useState, useEffect } from 'react';

interface FilterPanelProps {
  selectedMetroRegion: string;
  setSelectedMetroRegion: (region: string) => void;
  selectedBasicRegions: string[];
  setSelectedBasicRegions: (regions: string[]) => void;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  viewMode: 'map' | 'table';
  setViewMode: (mode: 'map' | 'table') => void;
  favorites: string[];
  setFavorites: (favorites: string[]) => void;
}

export default function FilterPanel({
  selectedMetroRegion,
  setSelectedMetroRegion,
  selectedBasicRegions,
  setSelectedBasicRegions,
  selectedCategory,
  setSelectedCategory,
  viewMode,
  setViewMode,
  favorites,
  setFavorites
}: FilterPanelProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showCustomFilter, setShowCustomFilter] = useState(false);

  const metroRegions = ['전국', '서울특별시', '부산광역시', '대구광역시', '인천광역시', '광주광역시', '대전광역시', '울산광역시', '세종특별자치시', '경기도', '강원도', '충청북도', '충청남도', '전라북도', '전라남도', '경상북도', '경상남도', '제주특별자치도'];
  
  const basicRegionsByMetro: Record<string, string[]> = {
    '서울특별시': ['종로구', '중구', '용산구', '성동구', '광진구', '동대문구', '중랑구', '성북구', '강북구', '도봉구', '노원구', '은평구', '서대문구', '마포구', '양천구', '강서구', '구로구', '금천구', '영등포구', '동작구', '관악구', '서초구', '강남구', '송파구', '강동구'],
    '부산광역시': ['중구', '서구', '동구', '영도구', '부산진구', '동래구', '남구', '북구', '해운대구', '사하구', '금정구', '강서구', '연제구', '수영구', '사상구', '기장군'],
    '대구광역시': ['중구', '동구', '서구', '남구', '북구', '수성구', '달서구', '달성군'],
    '인천광역시': ['중구', '동구', '미추홀구', '연수구', '남동구', '부평구', '계양구', '서구', '강화군', '옹진군'],
    '광주광역시': ['동구', '서구', '남구', '북구', '광산구'],
    '대전광역시': ['동구', '중구', '서구', '유성구', '대덕구'],
    '울산광역시': ['중구', '남구', '동구', '북구', '울주군'],
    '세종특별자치시': ['세종시'],
    '경기도': ['수원시', '성남시', '고양시', '용인시', '부천시', '안산시', '안양시', '남양주시', '화성시', '평택시', '의정부시', '시흥시', '파주시', '광명시', '김포시', '군포시', '광주시', '이천시', '양주시', '오산시', '구리시', '안성시', '포천시', '의왕시', '하남시', '여주시', '양평군', '동두천시', '과천시', '가평군', '연천군'],
    '강원도': ['춘천시', '원주시', '강릉시', '동해시', '태백시', '속초시', '삼척시', '홍천군', '횡성군', '영월군', '평창군', '정선군', '철원군', '화천군', '양구군', '인제군', '고성군', '양양군'],
    '충청북도': ['청주시', '충주시', '제천시', '보은군', '옥천군', '영동군', '증평군', '진천군', '괴산군', '음성군', '단양군'],
    '충청남도': ['천안시', '공주시', '보령시', '아산시', '서산시', '논산시', '계룡시', '당진시', '금산군', '부여군', '서천군', '청양군', '홍성군', '예산군', '태안군'],
    '전라북도': ['전주시', '군산시', '익산시', '정읍시', '남원시', '김제시', '완주군', '진안군', '무주군', '장수군', '임실군', '순창군', '고창군', '부안군'],
    '전라남도': ['목포시', '여수시', '순천시', '나주시', '광양시', '담양군', '곡성군', '구례군', '고흥군', '보성군', '화순군', '장흥군', '강진군', '해남군', '영암군', '무안군', '함평군', '영광군', '장성군', '완도군', '진도군', '신안군'],
    '경상북도': ['포항시', '경주시', '김천시', '안동시', '구미시', '영주시', '영천시', '상주시', '문경시', '경산시', '군위군', '의성군', '청송군', '영양군', '영덕군', '청도군', '고령군', '성주군', '칠곡군', '예천군', '봉화군', '울진군', '울릉군'],
    '경상남도': ['창원시', '진주시', '통영시', '사천시', '김해시', '밀양시', '거제시', '양산시', '의령군', '함안군', '창녕군', '고성군', '남해군', '하동군', '산청군', '함양군', '거창군', '합천군'],
    '제주특별자치도': ['제주시', '서귀포시']
  };
  
  const categories = ['전체', '법규환경', '민원규제', '경제활동친화성', '행정절차', '인허가', '세무환경'];

  const availableBasicRegions = selectedMetroRegion === '전국' ? [] : (basicRegionsByMetro[selectedMetroRegion] || []);

  // 광역자치단체 변경 시 기초자치단체 선택 초기화
  useEffect(() => {
    setSelectedBasicRegions([]);
  }, [selectedMetroRegion, setSelectedBasicRegions]);

  const handleBasicRegionToggle = (region: string) => {
    if (selectedBasicRegions.includes(region)) {
      setSelectedBasicRegions(selectedBasicRegions.filter(r => r !== region));
    } else {
      setSelectedBasicRegions([...selectedBasicRegions, region]);
    }
  };

  const handleFavoriteToggle = (region: string) => {
    if (favorites.includes(region)) {
      setFavorites(favorites.filter(fav => fav !== region));
    } else {
      setFavorites([...favorites, region]);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-6">
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center whitespace-nowrap">
        <i className="ri-filter-3-line text-blue-600 mr-2"></i>
        검색 및 필터링
      </h3>

      {/* 검색창 */}
      <div className="mb-4 sm:mb-6">
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 whitespace-nowrap">지자체 검색</label>
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="지자체명 또는 항목 입력"
            className="w-full pl-8 sm:pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm"
          />
          <i className="ri-search-line absolute left-2 sm:left-3 top-2.5 text-gray-400 text-sm"></i>
        </div>
      </div>

      {/* 지역 선택 1 - 광역자치단체 */}
      <div className="mb-4 sm:mb-6">
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 whitespace-nowrap">지역 선택 (광역자치단체)</label>
        <select
          value={selectedMetroRegion}
          onChange={(e) => setSelectedMetroRegion(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm pr-8"
        >
          {metroRegions.map(region => (
            <option key={region} value={region}>{region}</option>
          ))}
        </select>
      </div>

      {/* 지역 선택 2 - 기초자치단체 */}
      <div className="mb-4 sm:mb-6">
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
          <span className="whitespace-nowrap">지역 선택 (기초자치단체)</span>
          <span className="text-xs text-gray-500 ml-1 whitespace-nowrap">- 중복선택 가능</span>
        </label>
        <div className={`max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2 ${
          selectedMetroRegion === '전국' ? 'bg-gray-50' : ''
        }`}>
          {selectedMetroRegion === '전국' ? (
            <p className="text-xs sm:text-sm text-gray-500 p-2 whitespace-nowrap">광역자치단체를 먼저 선택해주세요.</p>
          ) : availableBasicRegions.length === 0 ? (
            <p className="text-xs sm:text-sm text-gray-500 p-2 whitespace-nowrap">선택 가능한 기초자치단체가 없습니다.</p>
          ) : (
            <div className="space-y-1">
              {availableBasicRegions.map(region => (
                <label key={region} className="flex items-center p-1 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedBasicRegions.includes(region)}
                    onChange={() => handleBasicRegionToggle(region)}
                    className="mr-2 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-xs sm:text-sm text-gray-700 whitespace-nowrap">{region}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        {selectedBasicRegions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {selectedBasicRegions.map(region => (
              <span key={region} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full whitespace-nowrap">
                {region}
                <button
                  onClick={() => handleBasicRegionToggle(region)}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  <i className="ri-close-line"></i>
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* 평가항목 선택 */}
      <div className="mb-4 sm:mb-6">
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 whitespace-nowrap">평가항목</label>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm pr-8"
        >
          {categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
      </div>

      {/* 보기 모드 */}
      <div className="mb-4 sm:mb-6">
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-3 whitespace-nowrap">보기 모드</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setViewMode('map')}
            className={`px-2 sm:px-3 py-2 text-xs font-medium rounded-lg transition-colors whitespace-nowrap ${
              viewMode === 'map' 
                ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <i className="ri-map-2-line mr-1"></i>
            지도
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`px-2 sm:px-3 py-2 text-xs font-medium rounded-lg transition-colors whitespace-nowrap ${
              viewMode === 'table' 
                ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <i className="ri-table-line mr-1"></i>
            표
          </button>
        </div>
      </div>

      {/* 즐겨찾기 */}
      <div className="mb-4 sm:mb-6">
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-3 whitespace-nowrap">즐겨찾기</label>
        <div className="space-y-2 max-h-32 overflow-y-auto">
          {favorites.length === 0 ? (
            <p className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">즐겨찾기한 지역이 없습니다.</p>
          ) : (
            favorites.map(region => (
              <div key={region} className="flex items-center justify-between p-2 bg-yellow-50 rounded-lg">
                <span className="text-xs sm:text-sm text-gray-700 whitespace-nowrap truncate">{region}</span>
                <button
                  onClick={() => handleFavoriteToggle(region)}
                  className="text-yellow-500 hover:text-yellow-600 flex-shrink-0"
                >
                  <i className="ri-star-fill"></i>
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 커스텀 필터 */}
      <div className="mb-4 sm:mb-6">
        <button
          onClick={() => setShowCustomFilter(!showCustomFilter)}
          className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <span className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">커스텀 필터</span>
          <i className={`ri-arrow-${showCustomFilter ? 'up' : 'down'}-s-line text-gray-500`}></i>
        </button>
        
        {showCustomFilter && (
          <div className="mt-3 p-3 border border-gray-200 rounded-lg space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1 whitespace-nowrap">등급 범위</label>
              <div className="flex space-x-2">
                <select className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs pr-6">
                  <option>A</option>
                  <option>B</option>
                  <option>C</option>
                  <option>D</option>
                </select>
                <span className="text-gray-400">~</span>
                <select className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs pr-6">
                  <option>A</option>
                  <option>B</option>
                  <option>C</option>
                  <option>D</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="flex items-center text-xs text-gray-600">
                <input type="checkbox" className="mr-2" />
                <span className="whitespace-nowrap">최근 변화 있음</span>
              </label>
            </div>
            
            <button className="w-full px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap">
              필터 저장
            </button>
          </div>
        )}
      </div>

      {/* 히스토리 보기 */}
      <button className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-100 text-gray-700 text-xs sm:text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap">
        <i className="ri-history-line mr-2"></i>
        변화 추적 히스토리
      </button>
    </div>
  );
}
